/**
 * Integrations catalog — static metadata for the tools Ideoz can connect to,
 * plus the permission areas each exposes. Connection is real (OAuth via the
 * broker); the per-area permissions are a UI mockup for now — the grant/save is
 * deferred, so nothing here is persisted to the backend yet.
 */

import {
  FileText,
  HardDrive,
  Mail,
  FileSpreadsheet,
  Presentation,
  MessageSquare,
  BarChart3,
  Kanban,
  type LucideIcon,
} from 'lucide-react';

/** Access level a permission area can be granted. */
export type PermLevel = 'no_access' | 'read' | 'write' | 'read_write';

export const PERM_LABEL: Record<PermLevel, string> = {
  no_access: 'No access',
  read: 'Read',
  write: 'Write',
  read_write: 'Read & write',
};

/** One toggleable capability within an integration (e.g. Notion → Pages). */
export interface PermissionArea {
  id: string;
  title: string;
  description: string;
  /** Allowed levels, in display order. */
  levels: PermLevel[];
  /** Level selected when the tool is first opened. */
  defaultLevel: PermLevel;
}

/**
 * One setup step. A plain string renders as-is; the object form supports inline
 * HTML (e.g. an `<a target="_blank">` link) in `html`, and a `copy` value that
 * shows the text with a copy button (for the fixed callback URL to paste).
 */
export type IntegrationInstructionStep =
  | string
  | {
      /** Step content, rendered as HTML (trusted — authored here, not user input). */
      html: string;
      /** When set, show this value with a copy button beneath the step. */
      copy?: string;
    };

/**
 * Manual setup steps a founder must follow to complete a connection (e.g. tools
 * that need an API key created on their side). Only set on tools that aren't a
 * pure one-click OAuth — the card then shows a "How to Enable" help button that
 * opens these in a dialog. Omit for straightforward tools.
 */
export interface IntegrationInstructions {
  /** Short line shown above the steps. */
  intro?: string;
  /** Ordered steps the founder follows. */
  steps: IntegrationInstructionStep[];
}

export interface Integration {
  /** Stable id === backend `provider` (e.g. "notion"). */
  id: string;
  name: string;
  category: string;
  icon: LucideIcon;
  /** One-liner on the list card. */
  summary: string;
  /** Longer description on the detail page. */
  description: string;
  /** Primary button label when not yet connected. */
  connectLabel: string;
  /** Permission areas shown on the detail page (mockup). */
  areas: PermissionArea[];
  /** Setup instructions for tools that need manual steps (optional). */
  instructions?: IntegrationInstructions;
}

const rw: PermLevel[] = ['no_access', 'read', 'write', 'read_write'];
const rwWrite: PermLevel[] = ['no_access', 'read', 'write'];
const readOnly: PermLevel[] = ['no_access', 'read'];

export const INTEGRATIONS: Integration[] = [
  {
    id: 'notion',
    name: 'Notion',
    category: 'Docs & wikis',
    icon: FileText,
    summary: 'Pull product docs into scope and push artifacts back.',
    description:
      'Let Ideoz read your product wiki for context and publish generated artifacts straight into a Notion database.',
    connectLabel: 'Connect Notion',
    areas: [
      {
        id: 'pages',
        title: 'Pages',
        description: 'Read page content for context, or publish artifacts as new pages.',
        levels: rw,
        defaultLevel: 'read_write',
      },
      {
        id: 'databases',
        title: 'Databases',
        description: 'Query databases and append rows for generated artifacts.',
        levels: rw,
        defaultLevel: 'read',
      },
      {
        id: 'comments',
        title: 'Comments',
        description: 'Leave review comments on pages Ideoz updated.',
        levels: rwWrite,
        defaultLevel: 'no_access',
      },
    ],
  },

  {
    id: 'jira',
    name: 'Jira',
    category: 'Issue tracking',
    icon: Kanban,
    summary: 'Pull issues and projects into scope and push generated work items back.',
    description:
      'Let Ideoz read your Jira projects and issues for context, and create or update issues from generated artifacts.',
    connectLabel: 'Connect Jira',
    areas: [
      {
        id: 'issues',
        title: 'Issues',
        description: 'Read issue details for context, or create and update issues.',
        levels: rw,
        defaultLevel: 'read_write',
      },
      {
        id: 'projects',
        title: 'Projects',
        description: 'View project metadata, boards, and workflows.',
        levels: rw,
        defaultLevel: 'read',
      },
      {
        id: 'comments',
        title: 'Comments',
        description: 'Leave comments on issues Ideoz created or updated.',
        levels: rwWrite,
        defaultLevel: 'no_access',
      },
    ],
    instructions: {
      intro:
        'Please follow the steps below in your Jira (Atlassian) organization to allow the connection between Jira and Ideoz.',
      steps: [
        {
          html: 'Go to <a href="https://admin.atlassian.com" target="_blank" rel="noopener noreferrer">Atlassian Administration</a>, and select your organization if you have more than one.',
        },
        {
          html: 'Select <strong>Rovo</strong>, then <strong>Rovo MCP server</strong>.',
        },
        {
          html: 'Select <strong>Add domain</strong>.',
        },
        {
          html: 'Add the Ideoz callback domain:',
          copy: 'https://integration.ideoz.ai/api/v1/auth/jira/callback',
        },
      ],
    },
  },
  {
    id: 'mixpanel',
    name: 'Mixpanel',
    category: 'Analytics',
    icon: BarChart3,
    summary: 'Read your product analytics so Ideoz can summarize and analyze them.',
    description:
      'Let Ideoz read your Mixpanel events, reports, and metrics to analyze how users behave in your product.',
    connectLabel: 'Connect Mixpanel',
    areas: [
      {
        id: 'reports',
        title: 'Reports & insights',
        description: 'Read insights, funnels, flows, and retention results.',
        levels: readOnly,
        defaultLevel: 'read',
      },
      {
        id: 'events',
        title: 'Events & properties',
        description: 'Browse tracked events and properties to understand your data.',
        levels: readOnly,
        defaultLevel: 'read',
      },
    ],
    instructions: {
      intro:
        'Please follow the steps below in your Mixpanel organization to allow the connection between Mixpanel and Ideoz.',
      steps: [
        'Go to your profile settings.',
        {
          html: 'Select <strong>Organization settings</strong>.',
        },
        {
          html: 'Go to <strong>AI settings</strong> under your organization settings.',
        },
        {
          html: 'Toggle on <strong>Enable Mixpanel AI Features</strong>.',
        },
        {
          html: 'Toggle on <strong>Enable Mixpanel MCP</strong>.',
        },
      ],
    },
  },
  {
    id: 'amplitude',
    name: 'Amplitude',
    category: 'Analytics',
    icon: BarChart3,
    summary: 'Read your product analytics so Ideoz can summarize and analyze them.',
    description:
      'Let Ideoz read your Amplitude charts, dashboards, and events to understand how users behave in your product.',
    connectLabel: 'Connect Amplitude',
    areas: [
      {
        id: 'charts',
        title: 'Charts & dashboards',
        description: 'Read charts, dashboards, and saved analyses.',
        levels: readOnly,
        defaultLevel: 'read',
      },
      {
        id: 'events',
        title: 'Events & properties',
        description: 'Browse tracked events and user properties to understand your data.',
        levels: readOnly,
        defaultLevel: 'read',
      },
    ],
  },
  {
    id: 'trello',
    name: 'Trello',
    category: 'Project management',
    icon: Kanban,
    summary: 'Pull boards and cards into scope and push generated work items back.',
    description:
      'Let Ideoz read your Trello boards, lists, and cards for context, and create or update cards from generated artifacts.',
    connectLabel: 'Connect Trello',
    areas: [
      {
        id: 'boards',
        title: 'Boards & lists',
        description: 'Read board structure and lists, or create and update them.',
        levels: rw,
        defaultLevel: 'read',
      },
      {
        id: 'cards',
        title: 'Cards',
        description: 'Read card details and comments for context, or create and update cards.',
        levels: rw,
        defaultLevel: 'read_write',
      },
      {
        id: 'workspaces',
        title: 'Workspaces & members',
        description: 'View workspace and member info to understand who owns what.',
        levels: readOnly,
        defaultLevel: 'read',
      },
    ],
    instructions: {
      intro:
        'Please follow the steps below in your Trello (Atlassian) organization to allow the connection between Trello and Ideoz.',
      steps: [
        {
          html: 'Go to <a href="https://admin.atlassian.com" target="_blank" rel="noopener noreferrer">Atlassian Administration</a>, and select your organization if you have more than one.',
        },
        {
          html: 'Select <strong>Rovo</strong>, then <strong>Rovo MCP server</strong>.',
        },
        {
          html: 'Select <strong>Add domain</strong>.',
        },
        {
          html: 'Add the Ideoz callback domain:',
          copy: 'https://integration.ideoz.ai/api/v1/auth/trello/callback',
        },
      ],
    },
  },
];

export const getIntegration = (id: string | undefined): Integration | undefined =>
  INTEGRATIONS.find((i) => i.id === id);

/** Default permission map (area id → level) for an integration. */
export const defaultPermissions = (integration: Integration): Record<string, PermLevel> =>
  Object.fromEntries(integration.areas.map((a) => [a.id, a.defaultLevel]));

/** Count of areas granted anything beyond "No access". */
export const enabledAreaCount = (
  integration: Integration,
  permissions: Record<string, PermLevel>
): number =>
  integration.areas.filter((a) => (permissions[a.id] ?? a.defaultLevel) !== 'no_access').length;
